import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";

export const useTideData = (station) => {
  const [tideData, setTideData] = useState([]);
  const [highTideData, setHighTideData] = useState([]);
  const [lowTideData, setLowTideData] = useState([]);
  const [stationName, setStationName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchStationName = async () => {
      if (!station || !currentUser) return;

      setLoading(true);
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(
          `/api/tides/name/${station}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const json = await response.json();
        setStationName(json);
      } catch (error) {
        console.error("Error fetching station name:", error);
        setError("Failed to load tide station information");
      }
    };
    fetchStationName();
  }, [station, currentUser]);

  useEffect(() => {
    const fetchTideData = async () => {
      if (!station || !currentUser) return;

      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(
          `/api/tides/${station}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const json = await response.json();
        setTideData(json);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching tide data:", error);
        setError("Failed to load tide data");
        setLoading(false);
      }
    };
    fetchTideData();
  }, [station, currentUser]);

  useEffect(() => {
    if (tideData.length > 0) {
      const today = new Date();
      const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
      const nextDay = new Date(tomorrow.getTime() + 24 * 60 * 60 * 1000);

      const filteredData = tideData.filter((item) => {
        const itemDate = new Date(item.t);
        return itemDate >= today && itemDate <= nextDay;
      });

      const prepared = filteredData.map((item) => ({
        t: new Date(item.t),
        v: parseFloat(item.v),
      }));

      const calculatedHighTideData = [];
      const calculatedLowTideData = [];

      for (let i = 1; i < prepared.length - 1; i++) {
        if (
          prepared[i].v > prepared[i - 1].v &&
          prepared[i].v > prepared[i + 1].v
        ) {
          calculatedHighTideData.push({
            time: prepared[i].t,
            height: prepared[i].v,
          });
        } else if (
          prepared[i].v < prepared[i - 1].v &&
          prepared[i].v < prepared[i + 1].v
        ) {
          calculatedLowTideData.push({
            time: prepared[i].t,
            height: prepared[i].v,
          });
        }
      }

      setHighTideData(calculatedHighTideData);
      setLowTideData(calculatedLowTideData);
    }
  }, [tideData]);

  const allTideEvents = [
    ...highTideData.map((item) => ({ ...item, type: "High" })),
    ...lowTideData.map((item) => ({ ...item, type: "Low" })),
  ];

  allTideEvents.sort((a, b) => a.time.getTime() - b.time.getTime());

  const groupEventsByDate = (events) => {
    const grouped = {};
    events.forEach((event) => {
      const dateKey = new Date(event.time).toDateString();
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(event);
    });
    return grouped;
  };

  const groupedEvents = groupEventsByDate(allTideEvents);

  const getNextTideEvent = () => {
    const now = new Date();
    for (const event of allTideEvents) {
      if (event.time > now) {
        return {
          type: event.type,
          time: event.time,
          height: event.height,
        };
      }
    }
    return null;
  };

  const nextTide = getNextTideEvent();

  return {
    tideData: allTideEvents,
    highTideData,
    lowTideData,
    stationName,
    loading,
    error,
    groupedEvents,
    nextTide,
  };
};
